import { GuestBusinessDetailsRecordSchema } from "@/api/guest-business-details";
import { axiosInstance } from "@/axios/interceptors";
import { ZodSentryReporter } from "@/lib/zod-sentry-reporter";
import { z } from "zod";

export const SigninUserPayloadSchema = z.object({
	email: z.string(),
	password: z.string(),
});
export type SigninUserPayload = z.infer<typeof SigninUserPayloadSchema>;

export const SigninUserWithOtpPayloadSchema = SigninUserPayloadSchema.extend({
	otp_code: z.string(),
});
export type SigninUserWithOtpPayload = z.infer<
	typeof SigninUserWithOtpPayloadSchema
>;

export const SigninResponseSchema = z.union([
	z.object({
		requires_2fa: z.literal(true),
	}),
	z.object({
		access_token: z.string(),
	}),
]);
export type SigninResponse = z.infer<typeof SigninResponseSchema>;

export const SigninWithOtpResponseSchema = z.object({
	access_token: z.string(),
});
export type SigninWithOtpResponse = z.infer<typeof SigninWithOtpResponseSchema>;

export async function loginUser(
	payload: SigninUserPayload,
): Promise<SigninResponse> {
	const response = await axiosInstance.post("/auth/guests", payload);
	return ZodSentryReporter.parse(SigninResponseSchema, response);
}

export async function loginUserWithOtp(
	payload: SigninUserWithOtpPayload,
): Promise<SigninWithOtpResponse> {
	const response = await axiosInstance.post("/auth/guests", payload);
	return ZodSentryReporter.parse(SigninWithOtpResponseSchema, response);
}

export const ENROLLMENT_STEPS = [
	"BIOMETRIC_ENROLLMENT",
	"IDENTITY_VERIFICATION",
	"EMAIL_VERIFICATION",
	"PHONE_VERIFICATION",
	"PERSONAL_INFO",
	"TERMS_AND_CONDITIONS",
	"RESIDENTIAL_INFO",
	"PIN",
] as const;

// Enum for enrollment steps
export const EnrollmentStep = z.enum(ENROLLMENT_STEPS);

// Schema for the enrollment record object
const EnrollmentRecordSchema = z.object({
	id: z.number().int().positive(),
	location_id: z.number().int().positive(),
	completed: z.boolean(),
	missing_steps: z.array(EnrollmentStep),
});

// Export the schemas

// Type inference
export type EnrollmentStepType = z.infer<typeof EnrollmentStep>;
export type EnrollmentRecord = z.infer<typeof EnrollmentRecordSchema>;

export const UserInformationSchema = z.object({
	id: z.number(),
	email: z.string().email(),
	phone_number: z.string(),
	first_name: z.string(),
	last_name: z.string(),
	date_of_birth: z.string(),
	address: z.string(),
	business_name: z.string().nullable(),
	apt_number: z.string(),
	city: z.string(),
	referral_code: z.string(),
	zipcode: z.string(),
	global_state_id: z.number(),
	global_country_id: z.number(),
	phone_global_country_id: z.number(),
	location_registration: z.null().or(EnrollmentRecordSchema),
	has_verified_identity: z.boolean(),
	has_verified_email: z.boolean(),
	has_verified_phone_number: z.boolean(),
	has_registered_face_id: z.boolean(),
	state: z
		.object({
			id: z.number(),
			name: z.string(),
			code: z.string(),
			global_country_id: z.number().nullable(),
		})
		.nullable()
		.default(null),
	guest_business_details: z.array(GuestBusinessDetailsRecordSchema).default([]),
});

export type UserInformation = z.infer<typeof UserInformationSchema>;

export const getUserInformation = async () => {
	const response = await axiosInstance.get("/guests/me");
	return ZodSentryReporter.parse(UserInformationSchema, response);
};
